import { createSlice } from '@reduxjs/toolkit';

const initialState = {
    loudnessLevel: -9,
    inputLoudnessLevel: -9,
    addLoudnessToFileNames: false,
    // The tab's primary action. Off + filenames off is a dead mode (the Match
    // button has nothing to do and sits disabled), so this defaults ON and
    // updateLoudnessSettings normalizes a persisted both-off pair back to it.
    changeLoudness: true,
    backupLoudnessOriginalFiles: true, // safe-by-default: keep originals untouched
    // Full measurable span. -70 (not -60) is ffmpeg's ebur128 floor, so a
    // -60 default silently hid every file quieter than that — with no slider
    // position that could bring them back.
    loudnessFilterMin: -70,
    loudnessFilterMax: 0,
    // Folder-source list order: 'name' = flat A-Z by displayed "Artist - Title";
    // 'folder' = grouped under subfolder headers. Same control as Compatibility.
    listGrouping: 'name',
    // Non-folder sources: 'added' = the source's own order; 'name' = A-Z by
    // displayed label (render-time). Same control as Compatibility.
    listSort: 'added'
};

const loudnessSettingsSlice = createSlice({
    name: 'loudnessSettings',
    initialState,
    reducers: {
        setLoudnessLevel(state, action) {
            state.loudnessLevel = action.payload;
        },
        setInputLoudnessLevel(state, action) {
            state.inputLoudnessLevel = action.payload;
        },
        setAddLoudnessToFileNames(state, action) {
            state.addLoudnessToFileNames = action.payload;
        },
        setChangeLoudness(state, action) {
            state.changeLoudness = action.payload;
        },
        updateLoudnessSettings(state, action) {
            const next = { ...state, ...action.payload };
            // Values persisted before changeLoudness defaulted on could leave
            // both actions off — nothing for Match to do. Restore the default.
            if (!next.changeLoudness && !next.addLoudnessToFileNames) next.changeLoudness = true;
            return next;
        },
        setBackupLoudnessOriginalFiles(state, action) {
            state.backupLoudnessOriginalFiles = action.payload;
        },
        setLoudnessFilterMin(state, action) {
            state.loudnessFilterMin = action.payload;
        },
        setLoudnessFilterMax(state, action) {
            state.loudnessFilterMax = action.payload;
        },
        setListGrouping(state, action) {
            state.listGrouping = action.payload === 'folder' ? 'folder' : 'name';
        },
        setListSort(state, action) {
            state.listSort = action.payload === 'name' ? 'name' : 'added';
        },
        // Startup hydrate from persisted settings (see settingsPersistenceConfig).
        // Name matters: the settings middleware skips '/hydrate' actions so the
        // load doesn't immediately re-write what it just read.
        hydrate(state, action) {
            return { ...state, ...action.payload };
        }
    },
});

export const {
    setLoudnessLevel,
    setInputLoudnessLevel,
    setAddLoudnessToFileNames,
    setChangeLoudness,
    updateLoudnessSettings,
    setBackupLoudnessOriginalFiles,
    setLoudnessFilterMin,
    setLoudnessFilterMax,
    setListGrouping,
    setListSort,
    hydrate
} = loudnessSettingsSlice.actions;

export default loudnessSettingsSlice.reducer;
